import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { List, Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import FinalizacaoVendaModal from "../FinalizacaoVendaModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, SwapHoriz, Add } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility"; // Ícone de spy
import useCompanySettings from "../../hooks/useSettings/companySettings";
import NewTicketModal from "../NewTicketModal";
import {
  Avatar,
  Badge,
  ListItemAvatar,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from "@material-ui/core";

// ENDOR AURORA · avatar com iniciais + gradiente determinístico pelo nome
const AVATAR_GRADIENTS = [
  ["#14b8a6", "#0d9488"],
  ["#f97316", "#ef4444"],
  ["#6366f1", "#4f46e5"],
  ["#ec4899", "#db2777"],
  ["#3b82f6", "#2563eb"],
  ["#10b981", "#059669"],
  ["#a855f7", "#7c3aed"],
];
const initialsOf = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (
    (parts[0][0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")
  ).toUpperCase();
};
const gradientOf = (name = "") => {
  let h = 0;
  const s = String(name || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const [a, b] = AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

const useStyles = makeStyles((theme) => ({
  // ENDOR AURORA · linha de ticket: pill arredondado, hover e selecionado
  ticket: {
    position: "relative",
    borderRadius: 12,
    margin: "3px 8px",
    width: "auto",
    gap: 12,
    paddingTop: 9,
    paddingBottom: 9,
    transition: "background-color .2s ease",
    "&:hover": {
      backgroundColor:
        theme.aurora && theme.aurora.alpha
          ? theme.aurora.alpha(theme.palette.primary.main, 0.06)
          : "rgba(0,0,0,0.04)",
    },
    "&:hover $rowActions": { opacity: 1, pointerEvents: "auto" },
    "&.Mui-selected": {
      backgroundColor:
        theme.aurora && theme.aurora.alpha
          ? theme.aurora.alpha(theme.palette.primary.main, 0.12)
          : "rgba(0,0,0,0.08)",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: "0 4px 4px 0",
        background: theme.palette.primary.main,
        zIndex: 2,
      },
    },
  },

  pendingTicket: {
    cursor: "unset",
  },
  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.5em",
    whiteSpace: "nowrap",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  newMessagesCount: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: 0,
    color: theme.palette.primary.main,
    fontWeight: "bold",
    marginRight: "10px",
    borderRadius: 0,
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 1,
    padding: 1,
    fontWeight: "bold",
    borderRadius: 3,
    fontSize: "0.6em",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "5px",
    fontWeight: "bold",
    color: (theme.aurora && theme.aurora.text1) || (theme.mode === "light" ? "black" : "white"),
  },

  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: -30,
    marginRight: "1px",
    color: (theme.aurora && theme.aurora.text3) || (theme.mode === "light" ? "black" : grey[400]),
  },

  lastMessageTimeUnread: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: -30,
    color: theme.palette.primary.main,
    fontWeight: "bold",
    marginRight: "1px",
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: "0%",
    marginLeft: "5px",
    color: (theme.aurora && theme.aurora.text2) || (theme.mode === "light" ? "black" : grey[400]),
  },

  contactLastMessageUnread: {
    paddingRight: 20,
    fontWeight: "bold",
    color: theme.mode === "light" ? "black" : grey[400],
    // width: "50%",
  },

  badgeStyle: {
    color: "white",
    backgroundColor: theme.palette.primary.main,
    fontWeight: 700,
    borderRadius: 10,
  },

  acceptButton: {
    position: "absolute",
    right: "1px",
  },

  ticketQueueColor: {
    flex: "none",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
  },

  ticketInfo: {
    position: "relative",
    top: -13,
  },
  secondaryContentSecond: {
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "nowrap",
    flexDirection: "row",
    alignContent: "flex-start",
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0,
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3,
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  connectionIcon: {
    marginRight: theme.spacing(1),
  },

  // Estilos para o modal da imagem
  imageModal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageModalContent: {
    outline: "none",
    maxWidth: "90vw",
    maxHeight: "90vh",
  },
  expandedImage: {
    width: "100%",
    height: "auto",
    maxWidth: "500px",
    borderRadius: theme.spacing(1),
  },
  clickableAvatar: {
    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
  },

  // ===== ENDOR AURORA · layout limpo da linha =====
  auroraRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
  },
  avatarWrap: { position: "relative", flex: "none" },
  avatarBig: {
    width: 46,
    height: 46,
    fontWeight: 700,
    color: "#fff",
  },
  onlineDot: {
    position: "absolute",
    bottom: -1,
    right: -1,
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#16a34a",
    border: `2.5px solid ${(theme.aurora && theme.aurora.paper) || theme.palette.background.paper}`,
  },
  rowMain: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 },
  rowLine1: { display: "flex", alignItems: "center", gap: 8 },
  rowName: {
    flex: 1,
    minWidth: 0,
    fontSize: 13.5,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: (theme.aurora && theme.aurora.text1) || theme.palette.text.primary,
  },
  rowTime: {
    flex: "none",
    fontSize: 11,
    fontWeight: 500,
    color: (theme.aurora && theme.aurora.text3) || theme.palette.text.secondary,
  },
  rowTimeUnread: { color: theme.palette.primary.main, fontWeight: 700 },
  rowLine2: { display: "flex", alignItems: "center", gap: 8 },
  rowPreview: {
    flex: 1,
    minWidth: 0,
    fontSize: 12.5,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: (theme.aurora && theme.aurora.text2) || theme.palette.text.secondary,
  },
  rowPreviewUnread: {
    fontWeight: 700,
    color: (theme.aurora && theme.aurora.text1) || theme.palette.text.primary,
  },
  unreadPill: {
    flex: "none",
    minWidth: 19,
    height: 19,
    padding: "0 6px",
    borderRadius: 10,
    background: theme.palette.primary.main,
    color: "#fff",
    fontSize: 10.5,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
  },
  rowTags: { display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginTop: 2 },
  miniTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 9.5,
    fontWeight: 700,
    lineHeight: 1.7,
    padding: "1px 8px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  },
  tagDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "currentColor",
    flex: "none",
  },
  rowActions: {
    position: "absolute",
    top: "50%",
    right: 10,
    transform: "translateY(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 2,
    opacity: 0,
    pointerEvents: "none",
    transition: "opacity .15s ease",
    background: (theme.aurora && theme.aurora.paper) || theme.palette.background.paper,
    borderRadius: 10,
    boxShadow: "0 2px 10px rgba(16,24,40,0.16)",
    padding: "2px 4px",
    zIndex: 3,
  },
  rowActionsShow: { opacity: 1, pointerEvents: "auto" },
  actionBtn: {
    backgroundColor: "transparent !important",
    boxShadow: "none !important",
    border: "none !important",
    padding: "4px !important",
    minWidth: "unset !important",
    width: "auto !important",
    borderRadius: "8px !important",
    fontSize: "0.6rem",
  },
}));

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [
    acceptTicketWithouSelectQueueOpen,
    setAcceptTicketWithouSelectQueueOpen,
  ] = useState(false);
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  // Estados para o modal de finalização de venda
  const [openFinalizacaoVenda, setOpenFinalizacaoVenda] = useState(false);
  const [finalizacaoTipo, setFinalizacaoTipo] = useState(null);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);
  const [showFinalizacaoOptions, setShowFinalizacaoOptions] = useState(false);

  const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem

  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);

  const { get: getSetting } = useCompanySettings();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Função para abrir modal da imagem
  const handleImageClick = (e) => {
    e.stopPropagation(); // Prevenir que o clique no avatar selecione o ticket
    if (ticket?.contact?.urlPicture) {
      setImageModalOpen(true);
    }
  };

  // Função para fechar modal da imagem
  const handleImageModalClose = () => {
    setImageModalOpen(false);
  };

  const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
    setAcceptTicketWithouSelectQueueOpen(true);
  }, []);

  const handleCloseTicket = async (id) => {
    // Verificar se a finalização com valor de venda está ativa
    if (
      user.finalizacaoComValorVendaAtiva === true ||
      user.finalizacaoComValorVendaAtiva === "true"
    ) {
      // Se estiver ativa, abrir o modal de finalização de venda
      setFinalizacaoTipo("comDespedida");
      setOpenFinalizacaoVenda(true);
      handleSelectTicket(ticket);
      history.push(`/tickets/${ticket.uuid}`);
    } else {
      // Comportamento original
      const setting = await getSetting({
        column: "requiredTag",
      });

      if (setting.requiredTag === "enabled") {
        //verificar se tem uma tag
        try {
          const contactTags = await api.get(
            `/contactTags/${ticket.contact.id}`
          );
          if (!contactTags.data.tags) {
            toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
          } else {
            await api.put(`/tickets/${id}`, {
              status: "closed",
              userId: user?.id || null,
            });

            if (isMounted.current) {
              setLoading(false);
            }

            history.push(`/tickets/`);
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      } else {
        setLoading(true);
        try {
          await api.put(`/tickets/${id}`, {
            status: "closed",
            userId: user?.id || null,
          });
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        history.push(`/tickets/`);
      }
    }
  };

  const handleCloseIgnoreTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        userId: user?.id || null,
        sendFarewellMessage: false,
        amountUsedBotQueues: 0,
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }

    history.push(`/tickets/`);
  };

  const truncate = (str, len) => {
    if (!isNil(str)) {
      if (str.length > len) {
        return str.substring(0, len) + "...";
      }
      return str;
    }
  };

  const handleCloseTransferTicketModal = useCallback(() => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  }, []);

  const handleOpenTransferModal = () => {
    setLoading(true);
    setTransferTicketModalOpen(true);
    if (isMounted.current) {
      setLoading(false);
    }
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenNewTicketModal = () => {
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket) {
      // Se um novo ticket foi criado, redirecionar para ele
      handleSelectTicket(newTicket);
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      const otherTicket = await api.put(`/tickets/${id}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: user?.id,
      });

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.data.user.name);
          setQueueTicketOpen(otherTicket.data.queue.name);
        } else {
          setLoading(false);
          setTabOpen(ticket.isGroup ? "group" : "open");
          handleSelectTicket(otherTicket.data);
          history.push(`/tickets/${otherTicket.uuid}`);
        }
      } else {
        let setting;

        try {
          setting = await getSetting({
            column: "sendGreetingAccepted",
          });
        } catch (err) {
          toastError(err);
        }

        if (
          setting.sendGreetingAccepted === "enabled" &&
          (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
        ) {
          handleSendMessage(ticket.id);
        }
        if (isMounted.current) {
          setLoading(false);
        }

        setTabOpen(ticket.isGroup ? "group" : "open");
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const handleSendMessage = async (id) => {
    let setting;

    try {
      setting = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      toastError(err);
    }
    if (!setting.greetingAcceptedMessage) {
      toast.warning(
        i18n.t("messagesList.header.buttons.greetingAcceptedMessage")
      );
      return;
    }
    const msg = `${setting.greetingAcceptedMessage}`;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: `${msg.trim()}`,
    };
    try {
      await api.post(`/messages/${id}`, message);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseAlert = useCallback(() => {
    setOpenAlert(false);
    setLoading(false);
  }, []);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleUpdateTicketStatusWithData = async (
    ticketData,
    sendFarewellMessage,
    finalizacaoMessage
  ) => {
    try {
      await api.put(`/tickets/${ticket.id}`, {
        ...ticketData,
        sendFarewellMessage,
        finalizacaoMessage,
      });
      toast.success("Ticket finalizado com sucesso!");
      history.push(`/tickets/`);
    } catch (err) {
      toastError(err);
    }
  };

  // Função para espionar ticket chatbot
  const handleSpyTicket = () => {
    handleSelectTicket(ticket);
    history.push(`/tickets/${ticket.uuid}`);
  };

  // Lógica de permissão para mensagens pending - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const shouldBlurMessages = ticket.status === "pending" && user?.allowSeeMessagesInPendingTickets === "disabled";

  // Função para renderizar a mensagem com base na permissão - MOVIDA PARA DEPOIS DE TODAS AS FUNÇÕES
  const renderLastMessage = () => {
    if (shouldBlurMessages) {
      return (
        <MarkdownWrapper>
          {i18n.t("tickets.messageHidden") || "Mensagem oculta"}
        </MarkdownWrapper>
      );
    }

    if (!ticket.lastMessage) {
      return <br />;
    }

    if (ticket.lastMessage.includes("data:image/png;base64")) {
      return <MarkdownWrapper>Localização</MarkdownWrapper>;
    }

    if (ticket.lastMessage.includes("BEGIN:VCARD")) {
      return <MarkdownWrapper>Contato</MarkdownWrapper>;
    }

    return (
      <MarkdownWrapper>
        {truncate(ticket.lastMessage, 40)}
      </MarkdownWrapper>
    );
  };

  return (
    <React.Fragment key={ticket.id}>
      {openAlert && (
        <ShowTicketOpen
          isOpen={openAlert}
          handleClose={handleCloseAlert}
          user={userTicketOpen}
          queue={queueTicketOpen}
        />
      )}
      {acceptTicketWithouSelectQueueOpen && (
        <AcceptTicketWithouSelectQueue
          modalOpen={acceptTicketWithouSelectQueueOpen}
          onClose={(e) => setAcceptTicketWithouSelectQueueOpen(false)}
          ticketId={ticket.id}
          ticket={ticket}
        />
      )}
      {transferTicketModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferTicketModalOpen}
          onClose={handleCloseTransferTicketModal}
          ticketid={ticket.id}
          ticket={ticket}
        />
      )}
      {newTicketModalOpen && (
        <NewTicketModal
          modalOpen={newTicketModalOpen}
          onClose={handleCloseNewTicketModal}
          initialContact={ticket.contact}
        />
      )}
      <ListItem
        button
        dense
        onClick={(e) => {
          console.log("e", e);
          const isCheckboxClicked =
            (e.target.tagName.toLowerCase() === "input" &&
              e.target.type === "checkbox") ||
            (e.target.tagName.toLowerCase() === "svg" &&
              e.target.type === undefined) ||
            (e.target.tagName.toLowerCase() === "path" &&
              e.target.type === undefined);

          if (isCheckboxClicked) return;

          handleSelectTicket(ticket);
        }}
        selected={ticketId && ticketId === ticket.uuid}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
        <ListItemAvatar className={classes.avatarWrap} style={{ minWidth: 0, margin: 0 }}>
          <Avatar
            src={
              ticket?.contact?.urlPicture &&
              !ticket.contact.urlPicture.includes("nopicture")
                ? ticket.contact.urlPicture
                : undefined
            }
            className={clsx(classes.avatarBig, classes.clickableAvatar)}
            style={{ background: gradientOf(ticket?.contact?.name) }}
            onClick={handleImageClick}
          >
            {initialsOf(ticket?.contact?.name)}
          </Avatar>
        </ListItemAvatar>

        <div className={classes.rowMain}>
          <div className={classes.rowLine1}>
            <span className={classes.rowName}>
              {ticket.isGroup && ticket.channel === "whatsapp" && (
                <GroupIcon style={{ color: grey[500], fontSize: 16 }} />
              )}
              {ticket.channel && (
                <ConnectionIcon
                  width="16"
                  height="16"
                  connectionType={ticket.channel}
                />
              )}
              {truncate(ticket.contact?.name, 40)}
            </span>
            {ticket.lastMessage && (
              <span
                className={clsx(classes.rowTime, {
                  [classes.rowTimeUnread]: Number(ticket.unreadMessages) > 0,
                })}
              >
                {isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
              </span>
            )}
          </div>

          <div className={classes.rowLine2}>
            <span
              className={clsx(classes.rowPreview, {
                [classes.rowPreviewUnread]: Number(ticket.unreadMessages) > 0,
              })}
            >
              {renderLastMessage()}
            </span>
            {Number(ticket.unreadMessages) > 0 && (
              <span className={classes.unreadPill}>
                {shouldBlurMessages ? "?" : ticket.unreadMessages}
              </span>
            )}
          </div>

          <div className={classes.rowTags}>
            {ticket?.whatsapp && (() => {
              const cor =
                ticket.channel === "whatsapp"
                  ? ticket.whatsapp?.color || "#25D366"
                  : ticket.channel === "facebook"
                  ? "#4267B2"
                  : "#E1306C";
              return (
                <span className={classes.miniTag} style={{ color: cor, backgroundColor: `${cor}22` }}>
                  <span className={classes.tagDot} />
                  {ticket.whatsapp?.name?.toUpperCase()}
                </span>
              );
            })()}
            {(() => {
              const cor = ticket.queue?.color || "#94a3b8";
              return (
                <span className={classes.miniTag} style={{ color: cor, backgroundColor: `${cor}22` }}>
                  <span className={classes.tagDot} />
                  {ticket.queueId
                    ? ticket.queue?.name?.toUpperCase()
                    : ticket.status === "lgpd"
                    ? "LGPD"
                    : `${i18n.t("momentsUser.noqueue")}`}
                </span>
              );
            })()}
            {ticket?.user && (
              <span
                className={classes.miniTag}
                style={{ color: "#475569", backgroundColor: "#47556922" }}
              >
                <span className={classes.tagDot} />
                {ticket.user?.name?.toUpperCase()}
              </span>
            )}
            {ticket?.contact?.tags?.map((tag) => (
              <ContactTag
                tag={tag}
                key={`ticket-contact-tag-${ticket.id}-${tag.id}`}
              />
            ))}
            {ticket.tags?.map((tag) => (
              <ContactTag
                tag={tag}
                key={`ticket-tag-${ticket.id}-${tag.id}`}
              />
            ))}
          </div>
        </div>
        <div className={classes.rowActions}>
          {ticket.status === "chatbot" && (
            <ButtonWithSpinner
              style={{ color: theme.mode === "light" ? "#2563eb" : "#FFF" }}
              variant="contained"
              className={classes.actionBtn}
              size="small"
              loading={loading}
              onClick={(e) => {
                e.stopPropagation();
                handleSpyTicket();
              }}
            >
              <Tooltip title="Espiar conversa do chatbot">
                <VisibilityIcon fontSize="small" />
              </Tooltip>
            </ButtonWithSpinner>
          )}
          {ticket.status !== "chatbot" && (
            <>
              {ticket.status === "pending" &&
                (ticket.queueId === null || ticket.queueId === undefined) && (
                  <ButtonWithSpinner
                    style={{ color: "#16a34a" }}
                    variant="contained"
                    className={classes.actionBtn}
                    size="small"
                    loading={loading}
                    onClick={(e) => handleOpenAcceptTicketWithouSelectQueue()}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                      <Done fontSize="small" />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              {ticket.status === "pending" && ticket.queueId !== null && (
                <ButtonWithSpinner
                  style={{ color: "#16a34a" }}
                  variant="contained"
                  className={classes.actionBtn}
                  size="small"
                  loading={loading}
                  onClick={(e) => handleAcepptTicket(ticket.id)}
                >
                  <Tooltip title={`${i18n.t("ticketsList.buttons.accept")}`}>
                    <Done fontSize="small" />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
              {(ticket.status === "pending" ||
                ticket.status === "open" ||
                ticket.status === "group") && (
                <ButtonWithSpinner
                  style={{ color: "#7c3aed" }}
                  variant="contained"
                  className={classes.actionBtn}
                  size="small"
                  loading={loading}
                  onClick={handleOpenTransferModal}
                >
                  <Tooltip title={`${i18n.t("ticketsList.buttons.transfer")}`}>
                    <SwapHoriz fontSize="small" />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
              {(ticket.status === "open" || ticket.status === "group") && (
                <ButtonWithSpinner
                  style={{ color: "#dc2626" }}
                  variant="contained"
                  className={classes.actionBtn}
                  size="small"
                  loading={loading}
                  onClick={(e) => handleCloseTicket(ticket.id)}
                >
                  <Tooltip title={`${i18n.t("ticketsList.buttons.closed")}`}>
                    <HighlightOff fontSize="small" />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
              {(ticket.status === "pending" || ticket.status === "lgpd") &&
                (user.userClosePendingTicket === "enabled" ||
                  user.profile === "admin") && (
                  <ButtonWithSpinner
                    style={{ color: "#dc2626" }}
                    variant="contained"
                    className={classes.actionBtn}
                    size="small"
                    loading={loading}
                    onClick={(e) => handleCloseIgnoreTicket(ticket.id)}
                  >
                    <Tooltip title={`${i18n.t("ticketsList.buttons.ignore")}`}>
                      <HighlightOff fontSize="small" />
                    </Tooltip>
                  </ButtonWithSpinner>
                )}
              {ticket.status === "closed" && (
                <ButtonWithSpinner
                  style={{ color: "#16a34a" }}
                  variant="contained"
                  className={classes.actionBtn}
                  size="small"
                  loading={loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenNewTicketModal();
                  }}
                >
                  <Tooltip title="Criar Novo Ticket">
                    <Add fontSize="small" />
                  </Tooltip>
                </ButtonWithSpinner>
              )}
            </>
          )}
        </div>
      </ListItem>

      {/* Modal de Finalização de Venda */}
      {openFinalizacaoVenda && (
        <FinalizacaoVendaModal
          open={openFinalizacaoVenda}
          onClose={() => setOpenFinalizacaoVenda(false)}
          ticket={ticket}
          onFinalizar={(ticketData) => {
            setOpenFinalizacaoVenda(false);
            setTicketDataToFinalize(ticketData);
            setShowFinalizacaoOptions(true);
          }}
        />
      )}

      {/* Modal de Opções de Finalização */}
      {showFinalizacaoOptions && (
        <Dialog
          open={showFinalizacaoOptions}
          onClose={() => setShowFinalizacaoOptions(false)}
          aria-labelledby="finalizacao-options-title"
        >
          <DialogTitle id="finalizacao-options-title">
            Como deseja finalizar?
          </DialogTitle>
          <DialogActions>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  false,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
            </Button>
            <Button
              onClick={async () => {
                setShowFinalizacaoOptions(false);
                await handleUpdateTicketStatusWithData(
                  ticketDataToFinalize,
                  true,
                  null
                );
              }}
              style={{ background: theme.palette.primary.main, color: "white" }}
            >
              {i18n.t("messagesList.header.dialogRatingCancel")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal da Imagem */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImageModalClose}
        className={classes.imageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogContent className={classes.imageModalContent}>
          <img 
            src={ticket?.contact?.urlPicture} 
            alt={ticket?.contact?.name || "Foto do contato"}
            className={classes.expandedImage}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TicketListItemCustom;